import { useAuth } from "@/contexts/AuthContext";
import { formatPrice } from "@/utils/currency";
import { toast } from "sonner";
import {
  ShoppingBag, Truck, Users
} from "lucide-react";
import { printReceipt, printFiscalReceipt } from "@/utils/printService";
import { StatusTimeline } from "./OrderDetailHelpers";
import { OrderDetailHeader } from "./OrderDetailHeader";
import { OrderCustomerInfo } from "./OrderCustomerInfo";
import { OrderDeliveryInfo } from "./OrderDeliveryInfo";
import { OrderItemsTable } from "./OrderItemsTable";
import { OrderPaymentSummary } from "./OrderPaymentSummary";
import { OrderStatusActions } from "./OrderStatusActions";
import { OrderDetailModals } from "./OrderDetailModals";
import { getNextStatus, getOrderTypeFlags } from "./orderDetailUtils";
import { useOrderDetailState } from "./useOrderDetailState";

export function OrderDetail({ order, deliverers, onUpdate, onNavigate, orderIndex, totalOrders, onClearSelection }) {
  const { authAxios } = useAuth();
  const s = useOrderDetailState({ order, authAxios, onUpdate, onClearSelection, formatPrice });
  const canEditItems = order.status !== "delivered" && order.status !== "cancelled";
  const { isDelivery, isPickup, isTable } = getOrderTypeFlags(order);
  const nextStatus = getNextStatus(order, isPickup, isTable);
  const subtotal = order.items.reduce((s, i) => s + i.quantity * i.unit_price, 0);
  const shipping = Number(order?.shipping_fee || 0);
  const deliverer = deliverers.find(d => d.id === order.deliverer_id);
  const orderNum = order.order_number || order.id.substring(0, 6).toUpperCase();
  const isFinished = order.status === "delivered" || order.status === "cancelled";

  const isOnlinePaid = (order.payment_method || "").toLowerCase().includes("online") || order.payment_status === "paid";
  const canEditDetails = !isOnlinePaid && order.status !== "cancelled";

  const _isPaid = order.payment_status === "paid" || !!order.paid_at || !!order.payment_confirmed_at;

  const showVivaReverify = !_isPaid && !order.hidden && (
    order.viva_order_code || order.viva_transaction_id || (order.payment_gateway || "").toLowerCase() === "viva"
  ) && (
    order.payment_status === "pending_verification" || order.status === "awaiting_payment"
  );

  const handlePrint = async () => {
    try {
      const result = await s.handlePrint(printReceipt);
      if (result.success) {
        toast.success(`Impresso com sucesso via ${result.method === "qz_tray" ? "QZ Tray" : "navegador"}!`);
      } else {
        toast.error(result.error || "Erro na impressao");
      }
    } catch (e) {
      toast.error("Erro ao imprimir: " + (e.message || e));
    }
  };

  const _buildInvoiceData = (vendusData) => ({
    invoice_number: vendusData.invoice_number || order.vendus_invoice_number || "",
    atcud:          vendusData.atcud          || order.vendus_atcud || "",
    qr_code:        vendusData.qr_code        || order.vendus_qr_code || "",
    doc_date:       vendusData.doc_date       || order.created_at || "",
    customer:       vendusData.customer       || { name: order.customer_name, nif: order.customer_nif || "" },
    items:          order.items || [],
    payment_method: order.payment_method || "",
    discount_amount: order.discount_amount || 0,
    total_gross:    vendusData.total_gross    || order.total || 0,
    total_tax:      vendusData.total_tax      || 0,
    taxes:          vendusData.taxes          || order.vendus_taxes || [],
  });

  const handlePrintFiscal = async (vendusData = {}) => {
    try {
      const invoiceData = _buildInvoiceData(vendusData);
      await printFiscalReceipt(invoiceData, s.restaurantData || {});
      toast.success("Fatura impressa!");
    } catch (e) {
      toast.error("Erro ao imprimir fatura: " + (e.message || e));
    }
  };

  const handleViewInvoicePdf = () => handlePrintFiscal();

  const handleCreateInvoice = async () => {
    if (order.vendus_invoice_number) {
      await handlePrintFiscal();
      return;
    }
    try {
      const res = await authAxios.post(`/vendus/invoice/${order.id}`);
      if (res.data.success) {
        toast.success(res.data.message || "Factura criada");
        onUpdate();
        await handlePrintFiscal(res.data);
      } else {
        toast.error(res.data.error || "Error creando factura");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Error conectando con Vendus"); }
  };

  const handleApplyDiscount = async () => {
    const num = parseFloat(s.discountInput) || 0;
    const computed = s.discountType === "percent" ? Math.min(subtotal, subtotal * num / 100) : Math.min(subtotal, num);
    const amount = Math.round(computed * 100) / 100;
    s.setApplyingDiscount(true);
    try {
      await authAxios.patch(`/orders/${order.id}/discount`, {
        discount_amount: amount,
        discount_type: s.discountType,
        discount_value: num,
        discount_reason: s.discountReason,
      });
      toast.success(amount > 0 ? `Desconto de ${formatPrice(amount)} aplicado` : "Desconto removido");
      s.setShowDiscountForm(false);
      s.setDiscountInput("");
      s.setDiscountReason("");
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao aplicar desconto"); }
    s.setApplyingDiscount(false);
  };

  const orderTypeLabel = isTable ? "Mesa" : isPickup ? "Retirada" : "Delivery";
  const orderTypeIcon = isTable ? <Users className="w-3.5 h-3.5" /> : isPickup ? <ShoppingBag className="w-3.5 h-3.5" /> : <Truck className="w-3.5 h-3.5" />;
  const orderTypeColor = isTable ? "bg-blue-100 text-blue-700 border-blue-200" : isPickup ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-emerald-100 text-emerald-700 border-emerald-200";

  return (
    <div className="flex flex-col bg-white min-w-0 w-full md:h-full" data-testid="order-detail-panel">
      {/* Header: wraps to 2 rows on mobile if needed */}
      <OrderDetailHeader order={order} orderIndex={orderIndex} totalOrders={totalOrders} onNavigate={onNavigate} orderNum={orderNum} orderTypeColor={orderTypeColor} orderTypeIcon={orderTypeIcon} orderTypeLabel={orderTypeLabel} isFinished={isFinished} isDelivery={isDelivery} deliverers={deliverers} dispatching={s.dispatching} handleQuickDispatch={s.handleQuickDispatch} handlePrint={handlePrint} canEditDetails={canEditDetails} cancelling={s.cancelling} openEditDialog={s.openEditDialog} handleDuplicate={s.handleDuplicate} handleCreateInvoice={handleCreateInvoice} handleFinalize={s.handleFinalize} setShowCancelDialog={s.setShowCancelDialog} setCancelReason={s.setCancelReason} setShowPaymentDialog={s.setShowPaymentDialog} setShowScheduleDialog={s.setShowScheduleDialog} setScheduleFor={s.setScheduleFor} />

      {/* Status Timeline */}
      <div className="px-5 py-4 border-b bg-white">
        <StatusTimeline order={order} />
      </div>

      <div className="md:flex-1 md:overflow-y-auto min-w-0">
        <div className="p-4 space-y-5 min-w-0 w-full pb-36">
          {/* CUSTOMER INFO */}
          <OrderCustomerInfo order={order} customerStats={s.customerStats} isPickup={isPickup} restaurantData={s.restaurantData} />

          {/* ENTREGADOR - Only for delivery orders */}
          <OrderDeliveryInfo isDelivery={isDelivery} deliverer={deliverer} order={order} setShowSwapDialog={s.setShowSwapDialog} />

          {/* CARRITO */}
          <OrderItemsTable order={order} canEditItems={canEditItems} editingItems={s.editingItems} localItems={s.localItems} setLocalItems={s.setLocalItems} setEditingItems={s.setEditingItems} startEditItems={s.startEditItems} saveItemChanges={s.saveItemChanges} openAddProduct={s.openAddProduct} changeQty={s.changeQty} removeItem={s.removeItem} subtotal={subtotal} shipping={shipping} formatPrice={formatPrice} showDiscountForm={s.showDiscountForm} setShowDiscountForm={s.setShowDiscountForm} discountInput={s.discountInput} setDiscountInput={s.setDiscountInput} discountType={s.discountType} setDiscountType={s.setDiscountType} discountReason={s.discountReason} setDiscountReason={s.setDiscountReason} handleApplyDiscount={handleApplyDiscount} applyingDiscount={s.applyingDiscount} setApplyingDiscount={s.setApplyingDiscount} authAxios={authAxios} onUpdate={onUpdate} />

          {/* PAGOS */}
          <OrderPaymentSummary order={order} formatPrice={formatPrice} setShowPaymentDialog={s.setShowPaymentDialog} authAxios={authAxios} onUpdate={onUpdate} handleDuplicate={s.handleDuplicate} handleCreateInvoice={handleCreateInvoice} handleViewInvoicePdf={handleViewInvoicePdf} />

          {order.notes && (
            <div>
              <h3 className="font-bold text-xs text-gray-500 uppercase tracking-wider mb-2">OBSERVACOES</h3>
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200 text-sm text-gray-700">{order.notes}</div>
            </div>
          )}

        </div>
      </div>
      <OrderStatusActions order={order} isDelivery={isDelivery} isFinished={isFinished} deliverers={deliverers} dispatching={s.dispatching} dispatchPhone={s.dispatchPhone} setDispatchPhone={s.setDispatchPhone} dispatchName={s.dispatchName} setDispatchName={s.setDispatchName} handleAssignDeliverer={s.handleAssignDeliverer} lastNotification={s.lastNotification} sendWhatsApp={s.sendWhatsApp} setLastNotification={s.setLastNotification} showVivaReverify={showVivaReverify} handleReverifyVivaPayment={s.handleReverifyVivaPayment} updateStatus={s.updateStatus} handleFinalize={s.handleFinalize} nextStatus={nextStatus} isPickup={isPickup} isTable={isTable} cancelling={s.cancelling} setCancelReason={s.setCancelReason} setShowCancelDialog={s.setShowCancelDialog} handleQuickDispatch={s.handleQuickDispatch} />
      <OrderDetailModals order={order} orderNum={orderNum} deliverers={deliverers} showSwapDialog={s.showSwapDialog} setShowSwapDialog={s.setShowSwapDialog} swapDeliverer={s.swapDeliverer} setSwapDeliverer={s.setSwapDeliverer} swapping={s.swapping} handleSwapDeliverer={s.handleSwapDeliverer} showPaymentDialog={s.showPaymentDialog} setShowPaymentDialog={s.setShowPaymentDialog} paymentMethods={s.paymentMethods} updatingPayment={s.updatingPayment} handleChangePayment={s.handleChangePayment} showAddProduct={s.showAddProduct} setShowAddProduct={s.setShowAddProduct} productSearch={s.productSearch} setProductSearch={s.setProductSearch} menuProducts={s.menuProducts} addProductToOrder={s.addProductToOrder} formatPrice={formatPrice} showEditDialog={s.showEditDialog} setShowEditDialog={s.setShowEditDialog} editName={s.editName} setEditName={s.setEditName} editPhone={s.editPhone} setEditPhone={s.setEditPhone} isDelivery={isDelivery} editAddress={s.editAddress} setEditAddress={s.setEditAddress} editNotes={s.editNotes} setEditNotes={s.setEditNotes} savingEdit={s.savingEdit} handleSaveEdit={s.handleSaveEdit} showScheduleDialog={s.showScheduleDialog} setShowScheduleDialog={s.setShowScheduleDialog} scheduleFor={s.scheduleFor} setScheduleFor={s.setScheduleFor} savingSchedule={s.savingSchedule} handleSaveSchedule={s.handleSaveSchedule} showCancelDialog={s.showCancelDialog} setShowCancelDialog={s.setShowCancelDialog} cancelReason={s.cancelReason} setCancelReason={s.setCancelReason} handleCancelConfirm={s.handleCancelConfirm} cancelling={s.cancelling} />
    </div>
  );
}
